
import {defineComponent} from "vue";
import {useToast} from "vue-toastification";
import {isEmptyString, truncateString} from "@/helpers/functions/string";
import {ErrorHandlerQueue} from "@/error/ErrorHandlerQueue";
import {DpExceptionsErrorHandler} from "@/error/handlers/DpExceptionsErrorHandler";
import {UIStateDto} from "@/dtos/UIStateDto";
import {AxiosErrorHandler} from "@/error/handlers/AxiosErrorHandler";
import AlertError2 from "@/components/UI/Bootstrap/Alert/AlertError2.vue";
import {PageDetailModeEnum} from "@/models/enum/PageDetailModeEnum";
import BaseOffcanvas from "@/components/UI/Bootstrap/Offcanvas/BaseOffcanvas.vue";
import {
  CustomerProductCategoryOverviewRestDto,
  CustomerProductCategoryOverviewResultsRestDto
} from "@/api/pharma-cpc-product-category-mgmt/models";
import {ProductCategoryRestService} from "@/services/rest/cpc-product-category-mgmt/ProductCategoryRestService";
import BaseOffcanvasButtonOpen from "@/components/UI/Bootstrap/Offcanvas/BaseOffcanvasButtonOpen.vue";
import CustomerCategory from "@/components/layouts/catalog/categories/CustomerCategory.vue";
import BaseTitle from "@/components/UI/BaseTitle.vue";
import BaseContainer from "@/components/UI/Bootstrap/BaseContainer.vue";
import {PropType} from "vue/dist/vue";
import {
  CustomerProductCategoryTreeNode
} from "@/models/api/pharma-cpc-product-category-mgmt/CustomerProductCategoryTreeNode";
import CustomerCategoryNode from "@/components/layouts/catalog/categories/CustomerCategoryNode.vue";

export default defineComponent({
  name: "CustomerCategories",
  components: {
    CustomerCategoryNode,
    BaseContainer,
    BaseTitle,
    CustomerCategory,
    BaseOffcanvasButtonOpen,
    BaseOffcanvas,
    AlertError2
  },
  props: {
    language: {
      type: String,
      required: true
    },
    allLanguages: {
      type: Array as PropType<string[]>,
      required: true
    },
  },
  data() {
    return {
      toaster: useToast(),
      pageUI: UIStateDto.createWithDefaults(),
      categories: null as CustomerProductCategoryOverviewResultsRestDto | null,
      categoryTree: [] as CustomerProductCategoryTreeNode[],
      categoryMode: PageDetailModeEnum.NEW as PageDetailModeEnum,
      categoryCode: null as string | null,
      categorySequence: 0 as number,
      open: new Set() as Set<string>,
    }
  },
  mounted() {
    this.reloadContent();
  },
  watch: {
    language: {
      handler() {
        this.reloadContent();
      },
      immediate: false
    }
  },
  computed: {
    PageDetailModeEnum() {
      return PageDetailModeEnum
    },
  },
  methods: {
    truncateString,
    isEmptyString,
    async reloadContent() {
      this.pageUI
        .clearError()
        .setNotReady();
      try {
        this.categories = await ProductCategoryRestService.getInstance().findCustomerProductCategories(this.language);
        this.initializeCategoryTree();
      } catch (exceptions: any) {
        this.setExceptions(exceptions);
        window.scrollTo(0, 0);
      } finally {
        this.pageUI.setReady();
      }
    },
    async afterSave(message: string | null = null) {
      this.toaster.success(message ?? this.$t('savedSuccessfully'));
      await this.reloadContent();
    },
    setExceptions(exceptions: unknown): void {
      ErrorHandlerQueue
        .create()
        .add(DpExceptionsErrorHandler.createWithDefaultUIStateBehavior(this.pageUI as UIStateDto))
        .add(AxiosErrorHandler.createWithDefaultUIStateBehavior(this.pageUI as UIStateDto))
        .catch(exceptions, true);
    },
    toggleOpen(code: string) {
      if (this.open.has(code)) {
        this.open.delete(code);
      } else {
        this.open.add(code);
      }
    },
    initializeCategoryTree(): void {
      if (!this.categories?.results || this.categories.results.length === 0) {
        this.categoryTree = [];
        return;
      }

      const nodeMap = new Map<string, CustomerProductCategoryTreeNode>();
      this.categoryTree = [];

      const ensureParentExists = (
        pathParts: string[],
        nameParts: string[],
        childPath: string
      ): void => {
        if (pathParts.length <= 1) return;

        const parentPathParts = pathParts.slice(0, -1);
        const parentPath = parentPathParts.join('|');

        if (!nodeMap.has(parentPath)) {
          const parentNode = new CustomerProductCategoryTreeNode(
            null,
            parentPathParts[parentPathParts.length - 1],
            nameParts[parentPathParts.length - 1],
            []
          );
          nodeMap.set(parentPath, parentNode);

          // If this parent has no grandparent (root level child), add to categoryTree immediately
          if (parentPathParts.length === 2) {
            this.categoryTree.push(parentNode);
          } else {
            ensureParentExists(parentPathParts, nameParts, parentPath);
          }
        }

        // Add child to parent
        const parentNode = nodeMap.get(parentPath)!;
        const childNode = nodeMap.get(childPath)!;
        if (!parentNode.children.includes(childNode)) {
          parentNode.children.push(childNode);
        }
      };

      for (const category of this.categories.results) {
        const pathParts = category.path_by_code.split('|');
        const nameParts = category.dp_path_by_name.split('|');

        if (pathParts.length > 2) {
          // Create node for this category
          const categoryNode = new CustomerProductCategoryTreeNode(
            category,
            category.code,
            nameParts[nameParts.length - 1],
            []
          );
          nodeMap.set(category.path_by_code, categoryNode);

          ensureParentExists(pathParts, nameParts, category.path_by_code);

        }
      }
    },
    prepareOffCanvasForNewCategory(): void {
      this.categoryCode = null;
      this.categoryMode = PageDetailModeEnum.NEW;
      this.categorySequence++;
    },
    prepareOffCanvasForEditCategory(category: CustomerProductCategoryOverviewRestDto): void {
      this.categoryCode = category.code;
      this.categoryMode = PageDetailModeEnum.EDIT;
      this.categorySequence++;
    },
    async deleteCategory(category: CustomerProductCategoryOverviewRestDto): Promise<void> {
      const answer = window.confirm(this.$t('deleteConfirmation'));

      if (answer) {
        this.pageUI
          .setNotReady()
          .clearError();

        try {
          await ProductCategoryRestService.getInstance().deleteCustomerProductCategory(category.code);
          await this.afterSave(this.$t('deletedSuccessfully'));
        } catch (exceptions: any) {
          this.setExceptions(exceptions);
        } finally {
          this.pageUI.setReady();
        }
      }
    },
  },
});
